import { useEffect, useMemo, useState } from 'react';
import { InternalUiRenderer } from '@erp/json-renderer/react';
import type { InternalUiSpec } from '@erp/json-renderer/react';
import { parseChartSpec, VegaChart } from '@erp/viz';
import { useAgentState } from '@/context/agent-state.context';
import { cn } from '@/lib/utils';
import { StreamingPreviewBoundary } from './StreamingPreviewBoundary';
import {
  parsePartialChartSpec,
  parsePartialInternalUiView,
  type PartialChartSpec,
  type PartialInternalUiView,
} from './partialChatSpecs';

type Preview =
  | {
      kind: 'view';
      toolCallId: string;
      seq: number;
      view: PartialInternalUiView;
    }
  | { kind: 'chart'; toolCallId: string; seq: number; chart: PartialChartSpec };

interface StreamingToolPreviewProps {
  className?: string;
}

/**
 * Live, in-chat preview of the UI-generation tools while the agent is still
 * writing them. Subscribes to `tool-input-delta` and progressively renders the
 * partial `render_custom_view` / `render_chart` spec, so the dashboard/chart
 * builds up element-by-element instead of popping in at the end.
 *
 * The preview stays up for the rest of the turn (it converges to the full spec
 * at the tool-input-end flush) and is gated off once the turn finishes
 * (`isLoading` false), exactly when ChatMessageList renders the authoritative
 * version from `msg.toolResults`. Holding it until then — rather than clearing
 * on `tool-result` — avoids the dashboard vanishing while the agent streams any
 * closing text before `finish`; the two renders show identical content, so the
 * handoff is seamless.
 *
 * Self-contained: it reads the stream via `useAgentState()`, so it just needs
 * to be mounted inside the chat's streaming region.
 */
export function StreamingToolPreview({ className }: StreamingToolPreviewProps) {
  const { isLoading, registerToolInputStreamListener } = useAgentState();
  const [preview, setPreview] = useState<Preview | null>(null);

  // Reset between turns: clearing on every isLoading edge drops the previous
  // turn's view so it can't flash before the next turn's first delta arrives.
  useEffect(() => {
    setPreview(null);
  }, [isLoading]);

  useEffect(() => {
    return registerToolInputStreamListener((toolCallId, toolName, partial) => {
      if (toolName === 'render_custom_view') {
        const view = parsePartialInternalUiView(partial);
        if (view) {
          setPreview((prev) => ({
            kind: 'view',
            toolCallId,
            seq: (prev?.seq ?? 0) + 1,
            view,
          }));
        }
      } else if (toolName === 'render_chart') {
        const chart = parsePartialChartSpec(partial);
        if (chart) {
          setPreview((prev) => ({
            kind: 'chart',
            toolCallId,
            seq: (prev?.seq ?? 0) + 1,
            chart,
          }));
        }
      }
    });
  }, [registerToolInputStreamListener]);

  if (!isLoading || !preview) return null;

  const resetKey = `${preview.toolCallId}:${preview.seq}`;

  return (
    <div className={cn('flex justify-start', className)}>
      <div className="w-full">
        <StreamingPreviewBoundary resetKey={resetKey}>
          {preview.kind === 'view' ? (
            <ViewPreview view={preview.view} />
          ) : (
            <ChartPreview chart={preview.chart} />
          )}
        </StreamingPreviewBoundary>
      </div>
    </div>
  );
}

function ViewPreview({ view }: { view: PartialInternalUiView }) {
  const spec = view.spec as unknown as InternalUiSpec;
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      {view.title && (
        <div className="mb-2 text-sm font-medium text-card-foreground">
          {view.title}
        </div>
      )}
      <InternalUiRenderer spec={spec} state={view.state} />
    </div>
  );
}

function ChartPreview({ chart }: { chart: PartialChartSpec }) {
  const parsed = useMemo(() => parseChartSpec(chart.spec), [chart.spec]);
  // A Vega-Lite spec only renders when reasonably complete — until then, show
  // nothing rather than an error (the next delta usually completes it).
  if (!parsed.ok) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      {chart.title && (
        <div className="mb-2 text-sm font-medium text-card-foreground">
          {chart.title}
        </div>
      )}
      <VegaChart spec={parsed.spec} height={260} />
    </div>
  );
}
